import { z } from 'zod';
import { Uuid } from './common.ts';

/** Identificador estable de bloque o escena (sobrevive a ediciones). */
export const BlockId = z.string().regex(/^[A-Za-z0-9_-]{4,64}$/, 'Id de bloque estable');
export const SceneId = z.string().regex(/^[A-Za-z0-9_-]{2,64}$/, 'Id de escena');
/** Indicación explícita del creador; su ausencia conserva la voz natural. */
export const VoiceEffect = z.enum(['walkie', 'echo']);
export type VoiceEffect = z.infer<typeof VoiceEffect>;

export const MAX_DOCUMENT_BYTES = 524_288;
export const MAX_BLOCKS = 5000;
export const MAX_SCENES = 2000;

// ---------------------------------------------------------------------------
// Guion: documento por bloques
// ---------------------------------------------------------------------------
export const ScriptBlock = z.object({
  id: BlockId,
  type: z.string().min(1).max(40),
  /** Texto plano del bloque para búsqueda, conteo y comparación. */
  text: z.string().max(100_000),
  voice_effect: VoiceEffect.optional(),
  /** Nodo del editor (Tiptap/ProseMirror JSON) sin el atributo blockId. */
  node: z.record(z.string(), z.unknown()).optional(),
});
export type ScriptBlock = z.infer<typeof ScriptBlock>;

export const ScriptDocument = z
  .object({
    schema_version: z.literal(1),
    type: z.literal('doc'),
    blocks: z.array(ScriptBlock).max(MAX_BLOCKS),
  })
  .superRefine((doc, ctx) => {
    const seen = new Set<string>();
    doc.blocks.forEach((b, i) => {
      if (seen.has(b.id)) ctx.addIssue({ code: 'custom', message: 'Id de bloque duplicado', path: ['blocks', i, 'id'] });
      seen.add(b.id);
    });
  });
export type ScriptDocument = z.infer<typeof ScriptDocument>;

export const ProductionRecoveryManifest = z.object({
 format:z.literal('promptcut.production-recovery.v1'),
 entries:z.array(z.object({
  title:z.string().min(1).max(200),source_label:z.string().min(1).max(300),source_hash:z.string().regex(/^[a-f0-9]{64}$/),
  source_kind:z.enum(['original_script','recovered_transcript']),
  publications:z.array(z.object({provider:z.enum(['youtube','facebook','instagram','tiktok']),account_id:z.string().min(1).max(200),external_id:z.string().min(1).max(200)})).min(1).max(12),
  script:ScriptDocument.optional(),
  production:z.record(z.string(),z.unknown()).refine(b=>JSON.stringify(b).length<=400_000,'Plan histórico demasiado grande').optional(),
  variants:z.array(z.object({slot:z.number().int().min(1).max(3),title:z.string().max(5000),description:z.string().max(5000),image:z.object({mime:z.enum(['image/webp','image/png','image/jpeg']),base64:z.string().max(3_000_000)}).optional()})).max(3).optional(),
 })).max(1000),
});
export type ProductionRecoveryManifest = z.infer<typeof ProductionRecoveryManifest>;

// ---------------------------------------------------------------------------
// Encargo (VideoBrief). Duración null = sin objetivo temporal (no cero).
// ---------------------------------------------------------------------------
export const StoryType = z.enum(['ensayo', 'reflexion', 'anecdota', 'cuento', 'explicacion', 'personalizado']);

export const BriefSource = z.object({
  title: z.string().min(1).max(300),
  url: z.url({ protocol: /^https?$/ }).max(2000).optional(),
  note: z.string().max(2000).optional(),
});

/** Permiso editorial sobre una versión concreta; no concede acceso al archivo. */
export const StockSelection = z.strictObject({
  asset_version_id: Uuid,
  title: z.string().trim().min(1).max(300),
  mode: z.enum(['allow', 'require']),
  range: z.strictObject({
    start_seconds: z.number().min(0).max(36_000),
    end_seconds: z.number().positive().max(36_000),
  }).refine(r => r.end_seconds > r.start_seconds, {
    message: 'end_seconds debe ser mayor que start_seconds', path: ['end_seconds'],
  }).optional(),
  note: z.string().max(1000).optional(),
});
export type StockSelection = z.infer<typeof StockSelection>;

export const VideoBrief = z.object({
  schema_version: z.literal(1),
  audience: z.string().max(2000).optional(),
  goal: z.string().max(2000).optional(),
  angle: z.string().max(2000).optional(),
  promise: z.string().max(2000).optional(),
  emotion: z.string().max(200).optional(),
  topics: z.array(z.string().trim().min(1).max(100)).max(30).optional(),
  content_path: z.object({matrix:z.string().trim().min(1).max(100),theme:z.string().trim().max(100).optional(),detail:z.string().trim().max(100).optional()}).optional(),
  music_cues: z.array(z.object({title:z.string().trim().min(1).max(200),asset_id:Uuid.optional(),note:z.string().max(1000).optional()})).max(50).optional(),
  stock_selections: z.array(StockSelection).max(50).superRefine((selections, ctx) => {
    const seen = new Set<string>();
    selections.forEach((selection, i) => {
      const id = selection.asset_version_id.toLowerCase();
      if (seen.has(id)) ctx.addIssue({ code: 'custom', message: 'Versión de stock duplicada', path: [i, 'asset_version_id'] });
      seen.add(id);
    });
  }).optional(),
  story_type: StoryType.optional(),
  story_type_custom: z.string().max(200).optional(),
  duration_target_seconds: z.number().positive().max(36_000).nullable().optional(),
  notes: z.string().max(20_000).optional(),
  sources: z.array(BriefSource).max(100).optional(),
  constraints: z.array(z.string().min(1).max(500)).max(100).optional(),
  /** Respuestas a preguntas adaptativas: clave de pregunta → respuesta libre. */
  answers: z.record(z.string().max(80), z.string().max(4000)).optional(),
});
export type VideoBrief = z.infer<typeof VideoBrief>;
/** Metadata-only import. Matching is explicit by provider, account and external ID. */
export const ContentIdentityManifest = z.object({ pieces: z.array(z.object({
  key: z.string().min(1).max(120), title: z.string().min(1).max(200), format: z.enum(['long','short']),
  topics: z.array(z.string().min(1).max(100)).max(30).optional(), source_key: z.string().optional(),
  publications: z.array(z.object({ provider: z.enum(['youtube','facebook','instagram','tiktok']),
    account_id: z.string().min(1), external_id: z.string().min(1).max(100) })).min(1).max(12),
})).max(1000) });

// ---------------------------------------------------------------------------
// Transcripción importada (tiempos en muestras enteras)
// ---------------------------------------------------------------------------
export const TranscriptWord = z.object({
  text: z.string().min(1).max(200),
  start_sample: z.int().min(0),
  end_sample: z.int().min(1),
});

export const TranscriptSegment = z
  .object({
    id: z.string().min(1).max(64),
    text: z.string().min(1).max(20_000),
    start_sample: z.int().min(0),
    end_sample: z.int().min(1),
    words: z.array(TranscriptWord).max(5000).optional(),
  })
  .refine((s) => s.end_sample > s.start_sample, { message: 'end_sample debe ser mayor que start_sample' });

export const Transcript = z.object({
  schema_version: z.literal(1),
  sample_rate: z.int().min(8000).max(384_000),
  language: z.string().max(10).optional(),
  source: z.string().max(120).optional(),
  segments: z.array(TranscriptSegment).max(20_000),
});
export type Transcript = z.infer<typeof Transcript>;

// ---------------------------------------------------------------------------
// Plan de edición (EditPlan v1). Fotogramas enteros con FPS racional; rangos de
// voz en muestras con frecuencia. La interfaz muestra segundos pero no reescribe.
// ---------------------------------------------------------------------------
export const Rational = z.object({ num: z.int().min(1), den: z.int().min(1) });
export type Rational = z.infer<typeof Rational>;

export const FrameRange = z
  .object({ start_frame: z.int().min(0), end_frame: z.int().min(1) })
  .refine((r) => r.end_frame > r.start_frame, { message: 'end_frame debe ser mayor que start_frame' });

export const SourceRange = z
  .object({ start: z.int().min(0), end: z.int().min(1), unit: z.enum(['frames', 'samples']) })
  .refine((r) => r.end > r.start, { message: 'end debe ser mayor que start' });

/** Zona normalizada 0..1 respecto al cuadro de salida. */
export const Zone = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  w: z.number().min(0).max(1),
  h: z.number().min(0).max(1),
});

export const ProtectedZone = Zone.extend({
  id: z.string().min(1).max(64),
  reason: z.string().max(200),
});

export const UsageRole = z.enum(['main', 'music', 'sfx', 'ambience', 'voice', 'overlay', 'avatar', 'background', 'other']);

export const AvatarPerformance = z.enum(['asomarse', 'entrar_borde', 'reaccion', 'inclinacion', 'giro_pausado', 'reposo']);

export const SceneText = z.object({
  kind: z.enum(['subtitle', 'wink', 'title']),
  content: z.string().max(2000),
  zone: Zone.optional(),
  max_lines: z.int().min(1).max(6).optional(),
  muted: z.boolean().optional(),
  reading_seconds: z.number().min(0).max(60).optional(),
});

export const SoundProcessing = z.strictObject({
  eq_bands: z.array(z.strictObject({
    type: z.enum(['bell', 'high_shelf', 'low_shelf']),
    frequency_hz: z.number().min(40).max(16_000),
    gain_db: z.number().min(-6).max(6),
    q: z.number().min(0.3).max(2),
  })).max(4).optional(),
  /** Segundos relativos al audio fuente después del recorte, no a la línea de tiempo. */
  phrase_lifts: z.array(z.strictObject({
    start_seconds: z.number().min(0),
    end_seconds: z.number().positive(),
    gain_db: z.number().min(0).max(3),
    ramp_seconds: z.number().min(0.2).max(1),
  }).superRefine((lift, ctx) => {
    if (lift.end_seconds <= lift.start_seconds) ctx.addIssue({ code: 'custom', path: ['end_seconds'], message: 'end_seconds debe ser mayor que start_seconds' });
    // Evita rechazar 1..1.4 con rampas .2 por redondeo binario de la resta.
    const roundoff = Number.EPSILON * Math.max(1, Math.abs(lift.start_seconds), Math.abs(lift.end_seconds)) * 2;
    if (2 * lift.ramp_seconds > lift.end_seconds - lift.start_seconds + roundoff) ctx.addIssue({ code: 'custom', path: ['ramp_seconds'], message: 'Las dos rampas deben caber dentro del tramo' });
  })).max(20).superRefine((lifts, ctx) => {
    lifts.forEach((lift, i) => {
      if (i > 0 && lift.start_seconds < lifts[i - 1]!.end_seconds) ctx.addIssue({ code: 'custom', path: [i, 'start_seconds'], message: 'Los tramos deben estar ordenados y no solaparse' });
    });
  }).optional(),
  makeup_gain_db: z.number().min(0).max(8).optional(),
  creative_effect: z.enum(['telephone', 'radio', 'walkie', 'distortion', 'echo']).optional(),
  peak_ceiling_db: z.number().min(-3).max(-1).default(-1.2),
});
export type SoundProcessing = z.infer<typeof SoundProcessing>;

export const SoundAction = z.object({
  asset_version_id: Uuid,
  role: UsageRole,
  source: SourceRange.optional(),
  output_start_frame: z.int().min(0).optional(),
  output_end_frame: z.int().min(1).optional(),
  gain_db: z.number().min(-96).max(24).optional(),
  processing: SoundProcessing.optional(),
  envelope: z.array(z.object({ at_frame: z.int().min(0), gain_db: z.number().min(-96).max(24) })).max(200).optional(),
  reason: z.string().max(500).optional(),
});

export const Scene = z.object({
  id: SceneId,
  chapter_id: z.string().max(64).nullable().optional(),
  intent: z.string().max(2000),
  track: z.enum(['main', 'overlay']).default('main'),
  recipe: z.object({ id: z.string().min(1).max(80), version: z.string().min(1).max(40) }).nullable().optional(),
  output: FrameRange,
  asset: z
    .object({
      asset_version_id: Uuid.nullable(),
      role: UsageRole.default('main'),
      source: SourceRange.nullable().optional(),
      /** Hueco declarado para un recurso aún no vinculado. */
      pending_slot: z.string().max(120).optional(),
    })
    .nullable()
    .optional(),
  anchors: z
    .array(z.object({ kind: z.enum(['word', 'beat', 'action', 'event', 'voice']), at_frame: z.int().min(0), label: z.string().max(200).optional() }))
    .max(500)
    .optional(),
  layers: z.array(z.record(z.string(), z.unknown())).max(50).optional(),
  avatar: z
    .object({
      performance: AvatarPerformance,
      seed: z.int().min(0),
      entry_edge: z.enum(['left', 'right', 'top', 'bottom']).optional(),
      occupancy: z.number().min(0).max(1).optional(),
      source_asset_version_id: Uuid.optional(),
    })
    .nullable()
    .optional(),
  texts: z.array(SceneText).max(50).optional(),
  transition: z.object({ kind: z.string().min(1).max(40), overlap_frames: z.int().min(0) }).nullable().optional(),
  sound_actions: z.array(SoundAction).max(100).optional(),
  cut_reason: z.string().max(500).optional(),
  status: z.enum(['draft', 'needs_asset', 'ready']).default('draft'),
});
export type Scene = z.infer<typeof Scene>;

export const Chapter = z.object({
  id: z.string().min(1).max(64),
  title: z.string().min(1).max(200),
  start_frame: z.int().min(0),
  end_frame: z.int().min(1),
  concept: z.string().max(2000).optional(),
  closing: z.string().max(2000).optional(),
  transition: z.string().max(200).optional(),
});

export const EditPlan = z.object({
  schema_version: z.literal(1),
  type: z.literal('edit_plan'),
  profile_version_id: Uuid.nullable().optional(),
  engine_required: z.object({ name: z.string().min(1).max(80), min_version: z.string().min(1).max(40) }),
  output: z.object({
    width: z.int().min(16).max(8192),
    height: z.int().min(16).max(8192),
    fps: Rational,
    sample_rate: z.int().min(8000).max(384_000).default(48_000),
    orientation: z.enum(['horizontal', 'vertical']),
    protected_zones: z.array(ProtectedZone).max(50).optional(),
  }),
  voice: z
    .object({ approved_take_ids: z.array(Uuid).max(200), sample_rate: z.int().min(8000).max(384_000).nullable() })
    .optional(),
  chapters: z.array(Chapter).max(200).optional(),
  scenes: z.array(Scene).max(MAX_SCENES),
  progression: z.record(z.string(), z.unknown()).optional(),
  dependencies: z
    .array(z.object({ scene_id: SceneId.optional(), kind: z.enum(['asset_pending', 'recipe', 'voice', 'other']), description: z.string().max(500) }))
    .max(500)
    .optional(),
});
export type EditPlan = z.infer<typeof EditPlan>;

/** Contenido de revisión discriminado por tipo. */
export const DocumentContent = z.union([ScriptDocument, EditPlan]);
export type DocumentContent = z.infer<typeof DocumentContent>;
